import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Activity,
  Award,
  BarChart3,
  CalendarDays,
  Download,
  Flag,
  Gauge,
  PieChart as PieChartIcon,
  Plus,
  RefreshCcw,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
  X,
  Zap,
} from 'lucide-react';

import { progress, goals, matches, trainings, exportData, wearables } from '../../lib/api';
import {
  getGoalDataUpdatedAt,
  getMatchDataUpdatedAt,
  getTrainingDataUpdatedAt,
  GOAL_DATA_UPDATED_EVENT,
  GOAL_DATA_UPDATED_KEY,
  MATCH_DATA_UPDATED_EVENT,
  MATCH_DATA_UPDATED_KEY,
  markGoalDataUpdated,
  TRAINING_DATA_UPDATED_EVENT,
  TRAINING_DATA_UPDATED_KEY,
} from '../../lib/dataFreshness';
import {
  Button,
  Field,
  Input,
  Select,
  Stack,
  Textarea,
} from '../../components/ui-kit';

const CHART_COLORS = ['#84CC16', '#18181B', '#06B6D4', '#A3E635', '#F59E0B', '#F43F5E'];
const PERIODS = ['7d', '30d', '90d', '1y'];

const TOOLTIP_STYLE = {
  backgroundColor: '#18181B',
  border: '1px solid rgba(190, 242, 100, 0.28)',
  borderRadius: '0.5rem',
  color: '#F9FAFB',
};

const emptyGoalDraft = () => ({
  title: '',
  description: '',
  category: 'serve_speed',
  target_value: '',
  unit: 'km/h',
  deadline: '',
});

const readDownloadFilename = (contentDisposition, fallbackName) => {
  if (!contentDisposition) return fallbackName;
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);
  const basicMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return basicMatch?.[1] || fallbackName;
};

const periodDays = (period) => {
  if (period === '7d') return 7;
  if (period === '90d') return 90;
  if (period === '1y') return 365;
  return 30;
};

const periodDateParams = (period) => {
  const dateTo = new Date();
  const dateFrom = new Date(dateTo.getTime() - periodDays(period) * 24 * 60 * 60 * 1000);
  return {
    date_from: dateFrom.toISOString(),
    date_to: dateTo.toISOString(),
  };
};

const formatMetric = (value, fallback = '-') =>
  value === null || value === undefined || value === '' ? fallback : value;

const numericValue = (value) => {
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
};

const compactNumber = (value, fallback = '-') => {
  const next = numericValue(value);
  if (next === null) return fallback;
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: next % 1 === 0 ? 0 : 1,
  }).format(next);
};

const minutesLabel = (value, t) => {
  const next = numericValue(value);
  if (next === null) return '-';
  if (next >= 60) {
    return `${compactNumber(next / 60)} ${t('progress.totalHours', 'hours')}`;
  }
  return `${compactNumber(next)} min`;
};

const hasWearableSummaryData = (summary) => {
  if (!summary) return false;
  return [
    summary.workouts_count,
    summary.total_duration_min,
    summary.active_calories_kcal,
    summary.steps,
    summary.sleep_duration_min,
    summary.avg_heart_rate_bpm,
  ].some((value) => numericValue(value) > 0);
};

const firstArrayItem = (value) => (Array.isArray(value) && value.length ? value[0] : null);

const pct = (value) =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? '-'
    : `${Number(value).toFixed(Number(value) % 1 === 0 ? 0 : 1)}%`;

const goalPercent = (goal) => {
  const current = Number(goal.current_value) || 0;
  const target = Number(goal.target_value) || 0;
  return target > 0 ? Math.min(Math.round((current / target) * 100), 100) : 0;
};

const averageGoalProgress = (goalsList) => {
  if (!goalsList.length) return null;
  return Math.round(
    goalsList.reduce((sum, goal) => sum + goalPercent(goal), 0) / goalsList.length,
  );
};

const parseDateMs = (value) => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

const hasTrainingAnalytics = (analytics) =>
  analytics?.trainings &&
  typeof analytics.trainings === 'object' &&
  analytics.trainings.total !== undefined;

const buildTrainingAnalyticsFallback = (trainingList, period, existingAnalytics = null) => {
  const days = periodDays(period);
  const startMs = Date.now() - days * 24 * 60 * 60 * 1000;
  const inPeriod = (Array.isArray(trainingList) ? trainingList : []).filter((training) => {
    const trainingMs = parseDateMs(training.date || training.created_at);
    return trainingMs !== null && trainingMs >= startMs;
  });
  const totalDuration = inPeriod.reduce(
    (sum, training) => sum + (Number(training.duration) || 0),
    0,
  );
  const byType = inPeriod.reduce((acc, training) => {
    const type = training.type || 'unknown';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});
  const weeklyTrend = inPeriod.reduce((acc, training) => {
    const sourceDate = training.date || training.created_at || '';
    const dateKey = String(sourceDate).slice(0, 10);
    if (!dateKey) return acc;
    if (!acc[dateKey]) acc[dateKey] = { count: 0, duration: 0 };
    acc[dateKey].count += 1;
    acc[dateKey].duration += Number(training.duration) || 0;
    return acc;
  }, {});

  return {
    ...(existingAnalytics || {}),
    period,
    trainings: {
      total: inPeriod.length,
      total_duration_minutes: totalDuration,
      total_duration_hours: Math.round((totalDuration / 60) * 10) / 10,
      by_type: byType,
    },
    statistics: existingAnalytics?.statistics || {
      avg_serve_speed: null,
      max_serve_speed: null,
      avg_accuracy: null,
      total_winners: 0,
      total_errors: 0,
      winners_errors_ratio: null,
    },
    weekly_trend: existingAnalytics?.weekly_trend || weeklyTrend,
  };
};

const SectionHeader = ({ kicker, title, action }) => (
  <div className="mb-4 flex items-end justify-between gap-4">
    <div>
      {kicker ? (
        <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
          {kicker}
        </p>
      ) : null}
      <h2 className="mt-1 text-xl font-black tracking-normal text-zinc-950">
        {title}
      </h2>
    </div>
    {action}
  </div>
);

const Panel = ({ children, className = '', testId }) => (
  <section
    className={`rounded-lg border border-zinc-200 bg-white shadow-sm ${className}`}
    data-testid={testId}
  >
    {children}
  </section>
);

const EmptyPanel = ({ icon, title, body }) => (
  <div className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-6 text-center">
    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-white text-zinc-500 shadow-sm">
      {icon}
    </div>
    <p className="mt-3 text-sm font-black text-zinc-950">{title}</p>
    <p className="mx-auto mt-1 max-w-sm text-sm leading-6 text-zinc-500">{body}</p>
  </div>
);

const MetricCard = ({ icon, label, value, detail, tone = 'lime', testId }) => {
  const toneClass =
    tone === 'rose'
      ? 'bg-rose-50 text-rose-700 ring-rose-100'
      : tone === 'cyan'
      ? 'bg-cyan-50 text-cyan-700 ring-cyan-100'
      : tone === 'zinc'
      ? 'bg-zinc-100 text-zinc-700 ring-zinc-200'
      : 'bg-lime-50 text-lime-700 ring-lime-100';

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-sm" data-testid={testId}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-zinc-500">{label}</p>
          <p className="mt-2 text-3xl font-black tracking-normal text-zinc-950">{value}</p>
        </div>
        <div className={`flex h-11 w-11 items-center justify-center rounded-lg ring-1 ${toneClass}`}>
          {icon}
        </div>
      </div>
      {detail ? <p className="mt-3 text-sm leading-5 text-zinc-500">{detail}</p> : null}
    </div>
  );
};

const ProgressSkeleton = () => (
  <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="progress-loading">
    <div className="mx-auto max-w-7xl space-y-5">
      <div className="h-56 animate-pulse rounded-lg bg-zinc-200" />
      <div className="grid gap-4 md:grid-cols-4">
        {[0, 1, 2, 3].map((item) => (
          <div key={item} className="h-36 animate-pulse rounded-lg bg-zinc-200" />
        ))}
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="h-80 animate-pulse rounded-lg bg-zinc-200" />
        <div className="h-80 animate-pulse rounded-lg bg-zinc-200" />
      </div>
    </div>
  </div>
);

const ErrorState = ({ onRetry, t }) => (
  <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="progress-page">
    <div className="mx-auto max-w-7xl">
      <Panel className="p-8 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-lg bg-rose-50 text-rose-700 ring-1 ring-rose-100">
          <RefreshCcw size={24} strokeWidth={1.8} />
        </div>
        <h1 className="mt-4 text-2xl font-black tracking-normal text-zinc-950">
          {t('progress.errorTitle', 'Progress data did not load')}
        </h1>
        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">
          {t('progress.errorBody', 'Try again to refresh analytics, goals, and match statistics.')}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-4 text-sm font-black text-white transition hover:bg-zinc-800"
        >
          <RefreshCcw size={16} strokeWidth={1.8} />
          {t('common.retry', 'Retry')}
        </button>
      </Panel>
    </div>
  </div>
);

const ProgressHeroCard = ({ analytics, matchStats, goalsList, period, t }) => {
  const totalTrainings = analytics?.trainings?.total || 0;
  const totalHours = analytics?.trainings?.total_duration_hours || 0;
  const totalMatches = matchStats?.total_matches || 0;
  const winRate = matchStats?.win_percentage ?? null;
  const goalAverage = averageGoalProgress(goalsList);
  const recordedSignals = totalTrainings + totalMatches + goalsList.length;

  return (
    <section className="relative overflow-hidden rounded-lg bg-zinc-950 p-5 text-white shadow-sm md:p-7">
      <div className="absolute inset-x-0 top-0 h-px bg-lime-300" />
      <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-lime-300 opacity-10 blur-3xl" />
      <div className="relative grid gap-6 lg:grid-cols-[1.15fr_0.85fr] lg:items-end">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-lime-300/30 bg-lime-300/10 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-lime-200">
            <Sparkles size={14} strokeWidth={1.8} />
            {t('progress.analytics', 'Analytics')}
          </div>
          <h1 className="mt-4 text-3xl font-black tracking-normal text-white md:text-4xl">
            {t('progress.title')}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300 md:text-base">
            {t('progress.subtitle')}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <span className="inline-flex min-h-11 items-center rounded-lg bg-white/10 px-3 text-sm font-bold text-zinc-100 ring-1 ring-white/10">
              {period}
            </span>
            <span className="inline-flex min-h-11 items-center rounded-lg bg-lime-300 px-3 text-sm font-black text-zinc-950">
              {totalTrainings} {t('progress.trainingsLabel', 'trainings')}
            </span>
          </div>
        </div>

        <div className="rounded-lg border border-white/10 bg-white/10 p-4">
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-200">
            {t('progress.performanceSnapshot', 'Performance snapshot')}
          </p>
          <div className="mt-4 flex items-end justify-between gap-4">
            <div>
              <p className="text-5xl font-black tracking-normal text-white">
                {recordedSignals}
              </p>
              <p className="mt-1 text-sm text-zinc-300">
                {recordedSignals === 0
                  ? t('progress.notEnoughData', 'Not enough data yet')
                  : t('progress.availableSignals', 'Based on available signals')}
              </p>
            </div>
            <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-lime-300 text-zinc-950">
              <Gauge size={30} strokeWidth={1.8} />
            </div>
          </div>
          <div className="mt-5 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-zinc-950/40 p-3">
              <p className="text-lg font-black text-white">{totalHours}</p>
              <p className="mt-1 text-xs text-zinc-400">{t('progress.totalHours', 'hours')}</p>
            </div>
            <div className="rounded-lg bg-zinc-950/40 p-3">
              <p className="text-lg font-black text-white">{winRate === null ? '-' : pct(winRate)}</p>
              <p className="mt-1 text-xs text-zinc-400">{t('progress.winRate', 'win rate')}</p>
            </div>
            <div className="rounded-lg bg-zinc-950/40 p-3">
              <p className="text-lg font-black text-white">{goalAverage === null ? '-' : pct(goalAverage)}</p>
              <p className="mt-1 text-xs text-zinc-400">{t('progress.goalsSection', 'goals')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const KeyMetrics = ({ analytics, matchStats, weeklyData, t }) => {
  const activeDays = weeklyData.filter((item) => item.trainings > 0).length;
  const days = periodDays(analytics?.period || '30d');

  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      <MetricCard
        icon={<Activity size={22} strokeWidth={1.8} />}
        label={t('progress.totalTrainings')}
        value={analytics?.trainings?.total || 0}
        detail={`${analytics?.trainings?.total_duration_hours || 0} ${t('progress.totalHours')}`}
        testId="progress-stat-trainings"
      />
      <MetricCard
        icon={<Zap size={22} strokeWidth={1.8} />}
        label={t('progress.serveSpeed')}
        value={formatMetric(analytics?.statistics?.avg_serve_speed)}
        detail={`${t('progress.maxServeSpeed')}: ${formatMetric(analytics?.statistics?.max_serve_speed)}`}
        tone="cyan"
        testId="progress-stat-serve"
      />
      <MetricCard
        icon={<Target size={22} strokeWidth={1.8} />}
        label={t('progress.accuracy')}
        value={analytics?.statistics?.avg_accuracy ? pct(analytics.statistics.avg_accuracy) : '-'}
        detail={t('progress.basedOnStats', 'Based on recorded statistics')}
        tone="zinc"
        testId="progress-stat-accuracy"
      />
      <MetricCard
        icon={<Award size={22} strokeWidth={1.8} />}
        label={t('progress.winsCount')}
        value={matchStats?.wins || 0}
        detail={`${pct(matchStats?.win_percentage || 0)} ${t('progress.winsPercentage')}`}
        tone={(matchStats?.win_percentage || 0) >= 50 ? 'lime' : 'rose'}
        testId="progress-stat-wins"
      />
      <MetricCard
        icon={<CalendarDays size={22} strokeWidth={1.8} />}
        label={t('progress.consistency', 'Consistency')}
        value={activeDays}
        detail={t('progress.activeDaysDetail', '{{days}} active days in {{period}} days', {
          days: activeDays,
          period: days,
        })}
        tone="lime"
      />
    </div>
  );
};

const WearableValueRow = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg bg-zinc-50 px-3 py-2">
      <span className="text-sm font-bold text-zinc-500">{label}</span>
      <span className="truncate text-right text-sm font-black text-zinc-950">{value}</span>
    </div>
  );
};

const WearableSnapshot = ({ title, rows, emptyLabel, icon }) => {
  const visibleRows = rows.filter((row) => row.value !== null && row.value !== undefined && row.value !== '');
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="font-black text-zinc-950">{title}</p>
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700">
          {icon}
        </div>
      </div>
      {visibleRows.length ? (
        <div className="space-y-2">
          {visibleRows.map((row) => (
            <WearableValueRow key={row.label} label={row.label} value={row.value} />
          ))}
        </div>
      ) : (
        <p className="rounded-lg bg-zinc-50 px-3 py-2 text-sm font-bold text-zinc-500">{emptyLabel}</p>
      )}
    </div>
  );
};

const WearableSummarySection = ({
  summary,
  recoverySnapshot,
  loadSnapshot,
  loading,
  failed,
  period,
  t,
}) => {
  const hasSummaryData = hasWearableSummaryData(summary);
  const hasRecoveryData = Boolean(recoverySnapshot);
  const hasLoadData = Boolean(loadSnapshot);
  const hasAnyData = hasSummaryData || hasRecoveryData || hasLoadData;
  const isEmpty = !loading && !failed && !hasAnyData;

  return (
    <Panel className="p-5" testId="progress-wearables">
      <SectionHeader
        kicker={t('progress.wearables.insights', 'Wearable insights')}
        title={t('progress.wearables.summary', 'Wearable summary')}
        action={
          <span className="rounded-full bg-zinc-100 px-3 py-1 text-xs font-black text-zinc-600 ring-1 ring-zinc-200">
            {t('progress.wearables.lastSyncedData', 'Last synced data')}: {period}
          </span>
        }
      />

      {loading ? (
        <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-sm font-bold text-zinc-500">
          {t('common.loading')}
        </div>
      ) : null}

      {failed ? (
        <div className="rounded-lg border border-rose-100 bg-rose-50 p-4 text-sm font-black text-rose-700">
          {t('progress.wearables.loadError', 'Failed to load wearable data')}
        </div>
      ) : null}

      {isEmpty ? (
        <EmptyPanel
          icon={<Activity size={22} strokeWidth={1.8} />}
          title={t('progress.wearables.emptyTitle', 'No wearable data yet')}
          body={t(
            'progress.wearables.emptyBody',
            'Connect and sync your watch from Profile to unlock wearable summaries.',
          )}
        />
      ) : null}

      {!loading && hasAnyData ? (
        <div className="space-y-4">
          {hasSummaryData ? (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              <MetricCard
                icon={<Activity size={20} strokeWidth={1.8} />}
                label={t('progress.wearables.workouts', 'Workouts')}
                value={compactNumber(summary?.workouts_count || 0)}
                detail={`${t('progress.wearables.workoutDuration', 'Wearable workouts')}: ${minutesLabel(summary?.total_duration_min, t)}`}
                tone="lime"
              />
              <MetricCard
                icon={<Zap size={20} strokeWidth={1.8} />}
                label={t('progress.wearables.activeCalories', 'Active calories')}
                value={summary?.active_calories_kcal === null || summary?.active_calories_kcal === undefined ? '-' : compactNumber(summary.active_calories_kcal)}
                detail="kcal"
                tone="cyan"
              />
              <MetricCard
                icon={<TrendingUp size={20} strokeWidth={1.8} />}
                label={t('progress.wearables.steps', 'Steps')}
                value={summary?.steps === null || summary?.steps === undefined ? '-' : compactNumber(summary.steps)}
                detail={t('progress.wearables.lastSyncedData', 'Last synced data')}
                tone="zinc"
              />
              <MetricCard
                icon={<ShieldCheck size={20} strokeWidth={1.8} />}
                label={t('progress.wearables.sleep', 'Sleep')}
                value={minutesLabel(summary?.sleep_duration_min, t)}
                detail={t('progress.wearables.recoverySnapshot', 'Recovery snapshot')}
                tone="lime"
              />
              <MetricCard
                icon={<Gauge size={20} strokeWidth={1.8} />}
                label={t('progress.wearables.averageHeartRate', 'Average heart rate')}
                value={summary?.avg_heart_rate_bpm === null || summary?.avg_heart_rate_bpm === undefined ? '-' : Math.round(Number(summary.avg_heart_rate_bpm))}
                detail="bpm"
                tone="rose"
              />
            </div>
          ) : null}

          <div className="grid gap-3 lg:grid-cols-2">
            <WearableSnapshot
              title={t('progress.wearables.recoverySnapshot', 'Recovery snapshot')}
              icon={<ShieldCheck size={18} strokeWidth={1.8} />}
              emptyLabel={t('progress.wearables.emptyTitle', 'No wearable data yet')}
              rows={[
                {
                  label: t('progress.wearables.sleep', 'Sleep'),
                  value: recoverySnapshot?.sleep_duration_min === undefined ? null : minutesLabel(recoverySnapshot.sleep_duration_min, t),
                },
                {
                  label: t('progress.wearables.restingHeartRate', 'Resting heart rate'),
                  value: recoverySnapshot?.resting_heart_rate_bpm === undefined ? null : `${compactNumber(recoverySnapshot.resting_heart_rate_bpm)} bpm`,
                },
                {
                  label: t('progress.wearables.hrv', 'HRV'),
                  value: recoverySnapshot?.hrv_rmssd_ms === undefined ? null : `${compactNumber(recoverySnapshot.hrv_rmssd_ms)} ms`,
                },
                {
                  label: t('progress.wearables.readiness', 'Readiness'),
                  value: recoverySnapshot?.readiness_score === undefined ? null : compactNumber(recoverySnapshot.readiness_score),
                },
              ]}
            />
            <WearableSnapshot
              title={t('progress.wearables.trainingLoadSnapshot', 'Training load snapshot')}
              icon={<BarChart3 size={18} strokeWidth={1.8} />}
              emptyLabel={t('progress.wearables.emptyTitle', 'No wearable data yet')}
              rows={[
                {
                  label: t('progress.wearables.loadLabel', 'Load'),
                  value: loadSnapshot?.load_label,
                },
                {
                  label: t('progress.wearables.duration', 'Duration'),
                  value: loadSnapshot?.wearable_workout_duration_min === undefined
                    ? null
                    : minutesLabel(loadSnapshot.wearable_workout_duration_min, t),
                },
                {
                  label: t('progress.wearables.loadRatio', 'Load ratio'),
                  value: loadSnapshot?.load_ratio === undefined ? null : compactNumber(loadSnapshot.load_ratio),
                },
              ]}
            />
          </div>
        </div>
      ) : null}
    </Panel>
  );
};

const PerformanceTrendChart = ({ data, t }) => (
  <Panel className="p-5">
    <SectionHeader
      kicker={t('progress.trend', 'Trend')}
      title={t('progress.weeklyTrend')}
      action={<BarChart3 size={20} className="text-zinc-400" strokeWidth={1.8} />}
    />
    {data.length === 0 ? (
      <EmptyPanel
        icon={<TrendingUp size={22} strokeWidth={1.8} />}
        title={t('progress.noTrendData', 'No trend data yet')}
        body={t('progress.noTrendBody', 'Record trainings in this period to see volume and consistency.')}
      />
    ) : (
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
            <XAxis dataKey="date" stroke="#71717A" tickLine={false} axisLine={false} />
            <YAxis stroke="#71717A" tickLine={false} axisLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(132, 204, 22, 0.08)' }} />
            <Bar dataKey="trainings" fill="#84CC16" name={t('progress.trainingsLabel')} radius={[6, 6, 0, 0]} />
            <Bar dataKey="duration" fill="#18181B" name={t('progress.durationHours')} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    )}
  </Panel>
);

const FocusAreaBreakdown = ({ data, t }) => (
  <Panel className="p-5">
    <SectionHeader
      kicker={t('progress.focus', 'Focus')}
      title={t('progress.trainingTypes')}
      action={<PieChartIcon size={20} className="text-zinc-400" strokeWidth={1.8} />}
    />
    {data.length === 0 ? (
      <EmptyPanel
        icon={<PieChartIcon size={22} strokeWidth={1.8} />}
        title={t('progress.noFocusData', 'No focus breakdown yet')}
        body={t('progress.noFocusBody', 'Training types will appear here once sessions are recorded.')}
      />
    ) : (
      <div className="grid gap-5 md:grid-cols-[0.9fr_1.1fr] md:items-center">
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} cx="50%" cy="50%" innerRadius={54} outerRadius={88} dataKey="value" paddingAngle={3}>
                {data.map((_entry, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-3">
          {data.map((item, index) => (
            <div key={item.name} className="flex items-center justify-between rounded-lg bg-zinc-50 px-3 py-2">
              <div className="flex min-w-0 items-center gap-3">
                <span
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                />
                <span className="truncate text-sm font-bold text-zinc-700">
                  {t(`training.types.${item.name}`, item.name)}
                </span>
              </div>
              <span className="text-sm font-black text-zinc-950">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </Panel>
);

const ShotStatsCard = ({ analytics, t }) => {
  const winners = analytics?.statistics?.total_winners || 0;
  const errors = analytics?.statistics?.total_errors || 0;
  const ratio = analytics?.statistics?.winners_errors_ratio;

  return (
    <Panel className="p-5">
      <SectionHeader
        kicker={t('progress.form', 'Form')}
        title={t('progress.shotStats')}
        action={<Zap size={20} className="text-zinc-400" strokeWidth={1.8} />}
      />
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg bg-lime-50 p-4 ring-1 ring-lime-100">
          <p className="text-xs font-black uppercase tracking-[0.12em] text-lime-700">{t('progress.winners')}</p>
          <p className="mt-2 text-2xl font-black text-zinc-950">{winners}</p>
        </div>
        <div className="rounded-lg bg-rose-50 p-4 ring-1 ring-rose-100">
          <p className="text-xs font-black uppercase tracking-[0.12em] text-rose-700">{t('progress.unforcedErrors')}</p>
          <p className="mt-2 text-2xl font-black text-zinc-950">{errors}</p>
        </div>
        <div className="rounded-lg bg-zinc-50 p-4 ring-1 ring-zinc-100">
          <p className="text-xs font-black uppercase tracking-[0.12em] text-zinc-500">{t('progress.ratio')}</p>
          <p className="mt-2 text-2xl font-black text-zinc-950">{formatMetric(ratio, '0.00')}</p>
        </div>
      </div>
    </Panel>
  );
};

const WinRateCard = ({ matchStats, t }) => {
  const total = matchStats?.total_matches || 0;
  const winRate = matchStats?.win_percentage || 0;
  const losses = matchStats?.losses || 0;

  return (
    <Panel className="p-5">
      <SectionHeader
        kicker={t('progress.matches', 'Matches')}
        title={t('progress.winRate', 'Win rate')}
        action={<Trophy size={20} className={winRate >= 50 ? 'text-lime-700' : 'text-rose-600'} strokeWidth={1.8} />}
      />
      {total === 0 ? (
        <EmptyPanel
          icon={<Trophy size={22} strokeWidth={1.8} />}
          title={t('progress.noMatchStats', 'No match statistics yet')}
          body={t('progress.noMatchStatsBody', 'Log match results to see win rate and surface trends.')}
        />
      ) : (
        <div>
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="text-5xl font-black tracking-normal text-zinc-950">{pct(winRate)}</p>
              <p className="mt-2 text-sm text-zinc-500">
                {matchStats?.wins || 0} {t('progress.winsCount')} / {losses} {t('match.lossesCount', 'losses')}
              </p>
            </div>
            <div className="flex h-16 w-16 items-center justify-center rounded-lg bg-lime-50 text-lime-700 ring-1 ring-lime-100">
              <ShieldCheck size={30} strokeWidth={1.8} />
            </div>
          </div>
          <div className="mt-5 h-3 rounded-full bg-zinc-100">
            <div className="h-3 rounded-full bg-lime-400" style={{ width: `${Math.min(winRate, 100)}%` }} />
          </div>
        </div>
      )}
    </Panel>
  );
};

const SurfaceStatsCard = ({ matchStats, t }) => {
  const entries = Object.entries(matchStats?.surface_stats || {});

  return (
    <Panel className="p-5">
      <SectionHeader
        kicker={t('progress.surface', 'Surface')}
        title={t('progress.surfaceStats')}
        action={<Activity size={20} className="text-zinc-400" strokeWidth={1.8} />}
      />
      {entries.length === 0 ? (
        <EmptyPanel
          icon={<Activity size={22} strokeWidth={1.8} />}
          title={t('progress.noSurfaceStats', 'No surface data yet')}
          body={t('progress.noSurfaceStatsBody', 'Surface performance appears after matches are logged.')}
        />
      ) : (
        <div className="space-y-3">
          {entries.map(([surface, stats]) => {
            const total = (stats.wins || 0) + (stats.losses || 0);
            const winRate = total ? Math.round(((stats.wins || 0) / total) * 100) : 0;
            return (
              <div key={surface} className="rounded-lg bg-zinc-50 p-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-black text-zinc-950">{t(`court.surfaces.${surface}`, surface)}</p>
                  <p className="text-sm font-bold text-zinc-500">
                    {stats.wins}W / {stats.losses}L
                  </p>
                </div>
                <div className="mt-3 h-2 rounded-full bg-white">
                  <div className="h-2 rounded-full bg-lime-400" style={{ width: `${winRate}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
};

const GoalItem = ({ goal, t }) => {
  const progressValue = goalPercent(goal);
  const completed = goal.status === 'completed';

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid={`goal-item-${goal.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-black text-zinc-950">{goal.title}</h4>
          {goal.description ? (
            <p className="mt-1 text-sm leading-5 text-zinc-500">{goal.description}</p>
          ) : null}
        </div>
        <span
          className={`shrink-0 rounded-full px-3 py-1 text-xs font-black ring-1 ${
            completed
              ? 'bg-lime-50 text-lime-700 ring-lime-100'
              : 'bg-zinc-100 text-zinc-700 ring-zinc-200'
          }`}
        >
          {t(`progress.status.${goal.status}`, goal.status)}
        </span>
      </div>
      <div className="mt-4 flex justify-between text-sm">
        <span className="text-zinc-500">{t('progress.progressLabel')}</span>
        <span className="font-bold text-zinc-950">
          {goal.current_value} / {goal.target_value} {goal.unit}
        </span>
      </div>
      <div className="mt-2 h-2 rounded-full bg-zinc-100" role="progressbar" aria-valuenow={progressValue} aria-valuemin="0" aria-valuemax="100">
        <div className="h-2 rounded-full bg-lime-400 transition-all" style={{ width: `${progressValue}%` }} />
      </div>
    </div>
  );
};

const GoalProgressSummary = ({ goalsList, onAdd, t }) => (
  <Panel className="p-5" testId="progress-goals">
    <SectionHeader
      kicker={t('progress.goals', 'Goals')}
      title={t('progress.goalsSection')}
      action={
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-lime-300 bg-lime-300 px-4 text-sm font-black text-zinc-950 transition hover:bg-lime-200"
          data-testid="goal-add-button"
        >
          <Plus size={16} strokeWidth={1.8} />
          {t('progress.addGoal')}
        </button>
      }
    />
    {goalsList.length === 0 ? (
      <EmptyPanel
        icon={<Flag size={22} strokeWidth={1.8} />}
        title={t('progress.noGoals')}
        body={t('progress.noGoalsBody', 'Create a goal to track progress toward a clear tennis outcome.')}
      />
    ) : (
      <div className="grid gap-3 md:grid-cols-2">
        {goalsList.map((goal) => (
          <GoalItem key={goal.id} goal={goal} t={t} />
        ))}
      </div>
    )}
  </Panel>
);

const StrengthWeaknessCard = ({ analytics, matchStats, goalsList, t }) => {
  const strengths = [];
  const focus = [];
  const accuracy = analytics?.statistics?.avg_accuracy;
  const winRate = matchStats?.win_percentage;
  const winners = analytics?.statistics?.total_winners || 0;
  const errors = analytics?.statistics?.total_errors || 0;
  const totalTrainings = analytics?.trainings?.total || 0;
  const goalAverage = averageGoalProgress(goalsList);

  if (totalTrainings > 0) strengths.push(t('progress.trainingVolumeSignal', 'Training volume is being recorded.'));
  if (accuracy >= 70) strengths.push(t('progress.accuracySignal', 'Accuracy is trending as a strength.'));
  if (winRate >= 60) strengths.push(t('progress.matchSignal', 'Match results are a strong signal.'));
  if (goalAverage >= 70) strengths.push(t('progress.goalSignal', 'Goal progress is moving well.'));

  if (totalTrainings === 0) focus.push(t('progress.addTrainingSignal', 'Add training sessions to build a trend line.'));
  if (accuracy && accuracy < 60) focus.push(t('progress.accuracyFocusSignal', 'Accuracy has room to improve.'));
  if (errors > winners) focus.push(t('progress.errorFocusSignal', 'Unforced errors are higher than winners.'));
  if ((matchStats?.total_matches || 0) === 0) focus.push(t('progress.addMatchesSignal', 'Log matches to unlock match analytics.'));

  return (
    <Panel className="p-5">
      <SectionHeader
        kicker={t('progress.insights', 'Insights')}
        title={t('progress.strengthWeakness', 'Strengths and focus')}
        action={<Sparkles size={20} className="text-lime-700" strokeWidth={1.8} />}
      />
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg bg-lime-50 p-4 ring-1 ring-lime-100">
          <p className="text-sm font-black text-lime-800">{t('progress.strengths', 'Strengths')}</p>
          <div className="mt-3 space-y-2">
            {(strengths.length ? strengths : [t('progress.noStrengthsYet', 'More data is needed before clear strengths appear.')]).map((item) => (
              <p key={item} className="text-sm leading-5 text-lime-900">{item}</p>
            ))}
          </div>
        </div>
        <div className="rounded-lg bg-zinc-50 p-4 ring-1 ring-zinc-100">
          <p className="text-sm font-black text-zinc-950">{t('progress.focusAreas', 'Focus areas')}</p>
          <div className="mt-3 space-y-2">
            {(focus.length ? focus : [t('progress.noFocusYet', 'Current data does not show a clear risk area.')]).map((item) => (
              <p key={item} className="text-sm leading-5 text-zinc-600">{item}</p>
            ))}
          </div>
        </div>
      </div>
    </Panel>
  );
};

const CoachInsightCard = ({ analytics, matchStats, weeklyData, t }) => {
  const totalTrainings = analytics?.trainings?.total || 0;
  const activeDays = weeklyData.filter((item) => item.trainings > 0).length;
  const winRate = matchStats?.win_percentage || 0;
  let insight = t('progress.insightStart', 'Start with one recorded training and one match result to unlock better guidance.');

  if (totalTrainings > 0 && activeDays <= 1) {
    insight = t('progress.insightConsistency', 'Build consistency first: spread sessions across more days in this period.');
  } else if ((matchStats?.total_matches || 0) > 0 && winRate < 50) {
    insight = t('progress.insightMatchReview', 'Review recent match notes and choose one focus area for the next block.');
  } else if (totalTrainings > 0) {
    insight = t('progress.insightMaintain', 'Your activity is visible. Keep logging sessions so trends become sharper.');
  }

  return (
    <Panel className="border-zinc-900 bg-zinc-950 p-5 text-white">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-lime-300 text-zinc-950">
          <Sparkles size={22} strokeWidth={1.8} />
        </div>
        <div>
          <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-200">
            {t('progress.coachInsight', 'Coach insight')}
          </p>
          <h2 className="mt-1 text-xl font-black tracking-normal text-white">
            {t('progress.nextBestAction', 'Next best action')}
          </h2>
          <p className="mt-3 text-sm leading-6 text-zinc-300">{insight}</p>
        </div>
      </div>
    </Panel>
  );
};

const NewGoalModal = ({ draft, setDraft, onSubmit, onClose, t }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-zinc-950/60 p-4"
    role="dialog"
    aria-modal="true"
    data-testid="goal-modal"
  >
    <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
      <div className="mb-4 flex items-center justify-between gap-4">
        <h3 className="text-lg font-black text-zinc-950">{t('progress.newGoal')}</h3>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={onClose}
          aria-label={t('common.cancel')}
          data-testid="goal-modal-close"
        >
          <X size={20} />
        </Button>
      </div>
      <form onSubmit={onSubmit}>
        <Stack gap={4}>
          <Field label={t('progress.goalTitle')} required>
            <Input
              type="text"
              data-testid="goal-input-title"
              value={draft.title}
              onChange={(event) => setDraft({ ...draft, title: event.target.value })}
              required
            />
          </Field>

          <Field label={t('progress.goalDescription')}>
            <Textarea
              data-testid="goal-input-description"
              value={draft.description}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              rows={2}
            />
          </Field>

          <Field label={t('progress.goalCategory')}>
            <Select
              data-testid="goal-input-category"
              value={draft.category}
              onChange={(event) => setDraft({ ...draft, category: event.target.value })}
            >
              {['serve_speed', 'accuracy', 'fitness', 'matches', 'trainings'].map((category) => (
                <option key={category} value={category}>
                  {t(`progress.categories.${category}`, category)}
                </option>
              ))}
            </Select>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label={t('progress.targetValue')} required>
              <Input
                type="number"
                data-testid="goal-input-target"
                value={draft.target_value}
                onChange={(event) =>
                  setDraft({ ...draft, target_value: parseFloat(event.target.value) })
                }
                required
              />
            </Field>
            <Field label={t('progress.unitLabel')}>
              <Input
                type="text"
                data-testid="goal-input-unit"
                value={draft.unit}
                onChange={(event) => setDraft({ ...draft, unit: event.target.value })}
                placeholder="km/h, %, times"
              />
            </Field>
          </div>

          <Field label={t('progress.deadline')}>
            <Input
              type="date"
              data-testid="goal-input-deadline"
              value={draft.deadline}
              onChange={(event) => setDraft({ ...draft, deadline: event.target.value })}
            />
          </Field>

          <Stack direction="row" gap={3} className="pt-2">
            <Button
              variant="secondary"
              block
              onClick={onClose}
              data-testid="goal-modal-cancel"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              block
              type="submit"
              data-testid="goal-modal-save"
            >
              {t('common.create')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </div>
  </div>
);

const ProgressPage = () => {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [goalsList, setGoalsList] = useState([]);
  const [matchStats, setMatchStats] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [wearableSummary, setWearableSummary] = useState(null);
  const [wearableRecovery, setWearableRecovery] = useState([]);
  const [wearableLoad, setWearableLoad] = useState([]);
  const [wearableLoading, setWearableLoading] = useState(false);
  const [wearableFailed, setWearableFailed] = useState(false);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [newGoal, setNewGoal] = useState(emptyGoalDraft);
  const lastSeenTrainingUpdateRef = useRef(getTrainingDataUpdatedAt());
  const lastSeenMatchUpdateRef = useRef(getMatchDataUpdatedAt());
  const lastSeenGoalUpdateRef = useRef(getGoalDataUpdatedAt());
  const freshnessRefreshInFlightRef = useRef(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadFailed(false);
      const [analyticsRes, goalsRes, matchStatsRes] = await Promise.allSettled([
        progress.getAnalytics(period),
        goals.getAll(),
        matches.getStats(),
      ]);

      let nextAnalytics = analyticsRes.status === 'fulfilled' ? analyticsRes.value.data : null;
      if (!hasTrainingAnalytics(nextAnalytics)) {
        try {
          const trainingsRes = await trainings.getAll({ limit: 500 });
          nextAnalytics = buildTrainingAnalyticsFallback(trainingsRes.data, period, nextAnalytics);
        } catch (fallbackError) {
          console.error('Failed to load training fallback for progress:', fallbackError);
        }
      }

      if (!nextAnalytics) {
        if (analyticsRes.status === 'rejected') {
          console.error('Failed to load progress analytics:', analyticsRes.reason);
        }
        setLoadFailed(true);
        return;
      }

      setAnalytics(nextAnalytics);

      if (goalsRes.status === 'fulfilled') {
        setGoalsList(Array.isArray(goalsRes.value.data) ? goalsRes.value.data : []);
      } else {
        console.error('Failed to load goals for progress:', goalsRes.reason);
      }

      if (matchStatsRes.status === 'fulfilled') {
        setMatchStats(matchStatsRes.value.data);
      } else {
        console.error('Failed to load match stats for progress:', matchStatsRes.reason);
      }

      lastSeenTrainingUpdateRef.current = getTrainingDataUpdatedAt();
      lastSeenMatchUpdateRef.current = getMatchDataUpdatedAt();
      lastSeenGoalUpdateRef.current = getGoalDataUpdatedAt();
    } catch (error) {
      console.error('Failed to load data:', error);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadWearableData = useCallback(async () => {
    setWearableLoading(true);
    setWearableFailed(false);
    try {
      const params = periodDateParams(period);
      const snapshotParams = { ...params, limit: 1 };
      const [summaryRes, recoveryRes, loadRes] = await Promise.allSettled([
        wearables.getSamplesSummary(params),
        wearables.getDailyRecovery(snapshotParams),
        wearables.getDailyLoad(snapshotParams),
      ]);

      if (summaryRes.status === 'fulfilled') {
        setWearableSummary(summaryRes.value.data || null);
      } else {
        setWearableSummary(null);
        setWearableFailed(true);
      }

      if (recoveryRes.status === 'fulfilled') {
        setWearableRecovery(Array.isArray(recoveryRes.value.data) ? recoveryRes.value.data : []);
      } else {
        setWearableRecovery([]);
        setWearableFailed(true);
      }

      if (loadRes.status === 'fulfilled') {
        setWearableLoad(Array.isArray(loadRes.value.data) ? loadRes.value.data : []);
      } else {
        setWearableLoad([]);
        setWearableFailed(true);
      }
    } catch (error) {
      setWearableSummary(null);
      setWearableRecovery([]);
      setWearableLoad([]);
      setWearableFailed(true);
    } finally {
      setWearableLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadWearableData();
  }, [loadWearableData]);

  useEffect(() => {
    const refetchIfAthleteProgressDataChanged = () => {
      const latestTrainingUpdate = getTrainingDataUpdatedAt();
      const latestMatchUpdate = getMatchDataUpdatedAt();
      const latestGoalUpdate = getGoalDataUpdatedAt();
      const trainingChanged =
        latestTrainingUpdate && latestTrainingUpdate !== lastSeenTrainingUpdateRef.current;
      const matchChanged =
        latestMatchUpdate && latestMatchUpdate !== lastSeenMatchUpdateRef.current;
      const goalChanged =
        latestGoalUpdate && latestGoalUpdate !== lastSeenGoalUpdateRef.current;

      if (!trainingChanged && !matchChanged && !goalChanged) return;
      if (freshnessRefreshInFlightRef.current) return;

      freshnessRefreshInFlightRef.current = true;
      Promise.resolve(loadData()).finally(() => {
        freshnessRefreshInFlightRef.current = false;
      });
    };

    const handleTrainingUpdated = () => {
      refetchIfAthleteProgressDataChanged();
    };

    const handleMatchUpdated = () => {
      refetchIfAthleteProgressDataChanged();
    };

    const handleGoalUpdated = () => {
      refetchIfAthleteProgressDataChanged();
    };

    const handleStorage = (event) => {
      if (
        event.key === TRAINING_DATA_UPDATED_KEY ||
        event.key === MATCH_DATA_UPDATED_KEY ||
        event.key === GOAL_DATA_UPDATED_KEY
      ) {
        refetchIfAthleteProgressDataChanged();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') refetchIfAthleteProgressDataChanged();
    };

    window.addEventListener(TRAINING_DATA_UPDATED_EVENT, handleTrainingUpdated);
    window.addEventListener(MATCH_DATA_UPDATED_EVENT, handleMatchUpdated);
    window.addEventListener(GOAL_DATA_UPDATED_EVENT, handleGoalUpdated);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', refetchIfAthleteProgressDataChanged);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener(TRAINING_DATA_UPDATED_EVENT, handleTrainingUpdated);
      window.removeEventListener(MATCH_DATA_UPDATED_EVENT, handleMatchUpdated);
      window.removeEventListener(GOAL_DATA_UPDATED_EVENT, handleGoalUpdated);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', refetchIfAthleteProgressDataChanged);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadData]);

  const handleCreateGoal = async (event) => {
    event.preventDefault();
    try {
      await goals.create(newGoal);
      setShowGoalModal(false);
      setNewGoal(emptyGoalDraft());
      freshnessRefreshInFlightRef.current = true;
      markGoalDataUpdated();
      await loadData();
      freshnessRefreshInFlightRef.current = false;
    } catch (error) {
      freshnessRefreshInFlightRef.current = false;
      console.error('Failed to create goal:', error);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await exportData.statisticsCSV();
      const contentType =
        response.headers?.['content-type'] || 'text/csv; charset=utf-8';
      const filename = readDownloadFilename(
        response.headers?.['content-disposition'],
        'statistics.csv',
      );
      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export:', error);
    }
  };

  const weeklyData = useMemo(
    () =>
      analytics?.weekly_trend
        ? Object.entries(analytics.weekly_trend).map(([date, data]) => ({
            date: date.slice(5),
            trainings: data.count,
            duration: Math.round(data.duration / 60),
          }))
        : [],
    [analytics],
  );

  const trainingTypeData = useMemo(
    () =>
      analytics?.trainings?.by_type
        ? Object.entries(analytics.trainings.by_type).map(([type, count]) => ({
            name: type,
            value: count,
          }))
        : [],
    [analytics],
  );

  if (loading) return <ProgressSkeleton />;
  if (loadFailed) return <ErrorState onRetry={loadData} t={t} />;

  return (
    <div className="p-4 lg:p-8 mb-20 lg:mb-0" data-testid="progress-page">
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.14em] text-lime-700">
              {t('common.athleteDiary', 'Athlete diary')}
            </p>
            <h1 className="mt-1 text-2xl font-black tracking-normal text-zinc-950 md:text-3xl">
              {t('progress.title')}
            </h1>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Select
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
              data-testid="progress-period-select"
              className="min-h-11 min-w-[7rem]"
            >
              {PERIODS.map((item) => (
                <option key={item} value={item}>{item}</option>
              ))}
            </Select>
            <button
              type="button"
              onClick={handleExportCSV}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-4 text-sm font-black text-white transition hover:bg-zinc-800"
              data-testid="progress-export-csv"
            >
              <Download size={16} strokeWidth={1.8} />
              CSV
            </button>
          </div>
        </div>

        <ProgressHeroCard
          analytics={analytics}
          matchStats={matchStats}
          goalsList={goalsList}
          period={period}
          t={t}
        />

        <KeyMetrics
          analytics={analytics}
          matchStats={matchStats}
          weeklyData={weeklyData}
          t={t}
        />

        <WearableSummarySection
          summary={wearableSummary}
          recoverySnapshot={firstArrayItem(wearableRecovery)}
          loadSnapshot={firstArrayItem(wearableLoad)}
          loading={wearableLoading}
          failed={wearableFailed}
          period={period}
          t={t}
        />

        <div className="grid gap-5 xl:grid-cols-[1.25fr_0.75fr]">
          <PerformanceTrendChart data={weeklyData} t={t} />
          <FocusAreaBreakdown data={trainingTypeData} t={t} />
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <WinRateCard matchStats={matchStats} t={t} />
          <ShotStatsCard analytics={analytics} t={t} />
        </div>

        <div className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
          <SurfaceStatsCard matchStats={matchStats} t={t} />
          <StrengthWeaknessCard
            analytics={analytics}
            matchStats={matchStats}
            goalsList={goalsList}
            t={t}
          />
        </div>

        <CoachInsightCard
          analytics={analytics}
          matchStats={matchStats}
          weeklyData={weeklyData}
          t={t}
        />

        <GoalProgressSummary
          goalsList={goalsList}
          onAdd={() => setShowGoalModal(true)}
          t={t}
        />
      </div>

      {showGoalModal && (
        <NewGoalModal
          draft={newGoal}
          setDraft={setNewGoal}
          onSubmit={handleCreateGoal}
          onClose={() => setShowGoalModal(false)}
          t={t}
        />
      )}
    </div>
  );
};

export default ProgressPage;
